import React from 'react'
import styled from '@emotion/styled'
import theme from '@nextretreat/ui-components/dist/Theme'
import { math, rem } from 'polished'
import PropTypes from 'prop-types'
import {
  BORDER_WIDTH,
  COLORS,
  fontSizes,
  fontWeights,
  radius,
  space,
} from 'Theme'
import { ReactComponent as ArrowheadIcon } from 'assets/images/svg/arrowhead.svg'
import Button from 'components/atoms/Button'
import { LoadingWrapper } from 'components/LoadingWrapper'
import { tripPopoverPassingProps } from 'sharedComponents/ModalManagement/TripPopover'
import { CELL_HEIGHT, CELL_WIDTH } from './constants'

const HEADER_UI_SIZE = rem('24px')
const BUTTON_SIZE = rem('32px')
const BUTTON_SIZE_INSET = rem('26px')
const ICON_SIZE = rem('12px')

export const HeaderTitle = styled('span')`
  overflow: hidden;

  font-size: ${fontSizes.l};
  font-weight: ${fontWeights.semi_bold};
  text-overflow: ellipsis;

  color: ${theme.COLORS.TXT_MAIN};
`

const StyledArrowheadIcon = styled(ArrowheadIcon)`
  width: ${ICON_SIZE};
  height: ${ICON_SIZE};

  ${({ direction }) => {
    const rotation = {
      down: 0,
      left: 90,
      up: 180,
      right: 270,
    }[direction || 'down']
    return `
      transform: rotate(${rotation}deg);
    `
  }}
`

export const ButtonGroup = styled('div')`
  display: flex;

  ${({ axis }) => {
    if (axis === 'horizontal') {
      return `
        margin-left: ${space.m};

        > :first-of-type {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        > :last-of-type {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        > :not(:last-of-type) {
          margin-right: -${BORDER_WIDTH};
        }
      `
    }

    if (axis === 'vertical') {
      return `
        flex-direction: column;

        margin-bottom: ${space.m};

        > :first-of-type {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        > :last-of-type {
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }

        > :not(:last-of-type) {
          margin-bottom: -${BORDER_WIDTH};
        }
      `
    }

    return ''
  }}
`

const StyledArrowButton = styled(Button.Tertiary)`
  min-width: 0;
  width: ${BUTTON_SIZE};
  height: ${BUTTON_SIZE};
  padding: 0;

  color: ${theme.COLORS.TXT_MAIN};

  &:disabled {
    opacity: 1 !important;
    filter: contrast(0.9);
    cursor: not-allowed;
  }

  ${({ isInset }) =>
    isInset
      ? `
        position: absolute;

        width: ${BUTTON_SIZE_INSET};
        height: ${BUTTON_SIZE_INSET};
      `
      : `
        @media (hover: hover) {
          &:hover {
            position: relative;
          }
        }
      `}

  ${({ direction, isInset }) => {
    const HALF_WIDTH = math(`${CELL_WIDTH} / 2`)
    const HALF_HEIGHT = math(`${CELL_HEIGHT} / 2`)

    if (isInset) {
      if (direction === 'left') {
        return `
          top: ${HALF_HEIGHT};
          left 0;

          transform: translate(-70%, -50%);
        `
      }
      if (direction === 'right') {
        return `
          top: ${HALF_HEIGHT};
          right ${CELL_WIDTH};

          transform: translate(70%, -50%);
        `
      }
      if (direction === 'up') {
        return `
          top: ${CELL_HEIGHT};
          right ${HALF_WIDTH};

          transform: translate(50%, -70%);
        `
      }
      if (direction === 'down') {
        return `
          bottom: 0;
          right ${HALF_WIDTH};

          transform: translate(50%, 70%);
        `
      }
    }

    return ''
  }}
`

export const ArrowButton = ({ isInset, isLoading, direction, ...props }) => (
  <StyledArrowButton isInset={isInset} direction={direction} {...props}>
    <LoadingWrapper loading={isLoading}>
      <StyledArrowheadIcon direction={direction} />
    </LoadingWrapper>
  </StyledArrowButton>
)

ArrowButton.propTypes = {
  isInset: PropTypes.bool,
  isLoading: PropTypes.bool,
  direction: PropTypes.string,
}

const StyledDropdownButton = styled(Button.Tertiary, {
  shouldForwardProp: (prop) => !tripPopoverPassingProps.includes(prop),
})`
  ${StyledArrowheadIcon} {
    margin-left: ${space.m};
  }
`

export const DropdownButton = React.forwardRef(
  ({ children, ...props }, ref) => (
    <StyledDropdownButton type="button" {...props} ref={ref}>
      {children}
      <StyledArrowheadIcon />
    </StyledDropdownButton>
  )
)

DropdownButton.propTypes = {
  children: PropTypes.node,
}

const HeaderUI = styled('div')`
  display: flex;
  align-items: center;

  border: ${BORDER_WIDTH} solid ${COLORS.BROTHER_BLUE};

  ${HeaderTitle} {
    background-color: ${theme.COLORS.WHITE};
  }

  ${({ position }) => {
    if (position === 'top') {
      return `
        grid-area: from;

        width: calc(100% - ${CELL_WIDTH});
        height: ${HEADER_UI_SIZE};
        padding: 0 ${space.s};
        margin-top: ${HEADER_UI_SIZE};

        border-bottom-width: 0;
        border-radius: ${radius.m} ${radius.m} 0 0;

        > * {
          transform: translateY(-${math(`${HEADER_UI_SIZE} / 2`)});
        }

        ${HeaderTitle} {
          padding: 0 ${space.s};
          margin-right: auto;
        }
      `
    }
    if (position === 'right') {
      return `
        flex-direction: column;
        grid-area: to;

        height: calc(100% - ${CELL_HEIGHT});
        width: ${HEADER_UI_SIZE};
        padding: ${space.s} 0;
        margin-top: auto;
        margin-right: ${HEADER_UI_SIZE};

        border-left: 0;
        border-radius: 0 ${radius.m} ${radius.m} 0;

        > * {
          transform: translateX(${math(`${HEADER_UI_SIZE} / 2`)});
        }

        ${HeaderTitle} {
          writing-mode: vertical-lr;

          padding: ${space.s} 0;
          margin-top: auto;
        }
      `
    }

    return ''
  }}
`

export default HeaderUI
